import { useProgress } from '@react-three/drei';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { ControlModes } from '../../helpers/enums';
import { createUseStyles } from 'react-jss';
import * as Icon from 'react-feather';
import { useThreeDState } from '../App/ThreeDContext';

const useStyles = createUseStyles({
  dialog: {
    width: 750,
    height: 350,
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 30,
    '@media (max-width: 700px)': {
      height: 450,
      paddingLeft: 8,
      paddingRight: 8,
      paddingTop: 8,
    },
  },
  label: {
    padding: '0 15% 30px 15%',
    textAlign: 'center',
    '@media (max-width: 700px)': {
      padding: '0 10% 30px 10%',
    },
  },
  button: {
    color: '#F0EDE6 !important',
    backgroundColor: '#27262B !important',
    border: '2px solid #F0EDE6 !important',
    alignSelf: 'center',
    '&:hover': {
      backgroundColor: '#131215 !important',
    },
  },
  instructions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    padding: 0,
    boxSizing: 'border-box',
    '@media (max-width: 700px)': {
      flexDirection: 'column',
    },
  },
  instruction: {
    width: 'unset',
    padding: 0,
    margin: 0,
    display: 'inline',
    boxSizing: 'border-box',
  },
  instructionImage: {
    position: 'relative',
    float: 'left',
    padding: 0,
    boxSizing: 'border-box',
  },
  instructionText: {
    float: 'left',
    padding: 0,
    paddingLeft: 20,
    boxSizing: 'border-box',
    '& p': {
      marginBlockStart: 0,
      marginBlockEnd: 0,
      position: 'relative',
      top: '50%',
      transform: 'translateY(-50%)',
      height: 'fit-content',
      fontSize: 20,
    },
  },
  rotateImage: {
    height: 85,
    width: 85,
  },
  navigateImage: {
    height: 65,
    width: 100,
  },
  rotateText: {
    height: 85,
    width: 170,
  },
  navigateText: {
    height: 65,
    width: 180,
  },
  grabIcon: {
    position: 'absolute',
    top: 29,
    left: 32,
  },
  triangle: {
    fill: '#0C0B0F',
    width: 21,
    height: 21,
  },
  arrowUp: {
    position: 'absolute',
    top: 0,
    left: 32,
  },
  arrowRight: {
    position: 'absolute',
    top: 32,
    left: 64,
    transform: 'rotate(90deg)',
  },
  arrowDown: {
    position: 'absolute',
    top: 64,
    left: 32,
    transform: 'rotate(180deg)',
  },
  arrowLeft: {
    position: 'absolute',
    top: 32,
    left: 0,
    transform: 'rotate(270deg)',
  },
});

export const HelpDialog = ({ onClose }) => {
  const { controlMode } = useThreeDState();
  const { active } = useProgress();
  const styles = useStyles();

  return (
    <Dialog
      open={!active}
      className={styles.dialog}
    >
      <div className={styles.instructions}>
        {controlMode === ControlModes.ORBIT_CONTROL &&
          <>
            <div className={styles.instruction}>
              <div className={`${styles.instructionImage} ${styles.rotateImage}`}>
                <svg
                  width="24"
                  height="26"
                  viewBox="0 0 54 56"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={styles.grabIcon}
                >
                  <path
                    d="M38.9894 51.1459L49.4261 40.7092C54.6794 35.4558 54.6794 26.9057 49.4261 21.6524L45.6296 17.8559V11.1013C45.6296 7.35773 42.5826 4.31077 38.839 4.31077C37.414 4.30865 36.0546 4.74484 34.9154 5.55793C34.3035 2.43898 31.5487 0.0780556 28.2519 0.0759378C25.2706 0.0759374 22.7361 2.00914 21.8235 4.6792C20.6737 3.78353 19.2296 3.25418 17.6648 3.25206C14.0335 3.24995 11.0585 6.11904 10.8828 9.70806C9.77112 8.95215 8.4562 8.54349 7.07777 8.5456C3.33206 8.54349 0.285094 11.5904 0.287213 15.3362L0.287215 36.4002C0.287212 41.626 2.32205 46.5405 6.01906 50.2375C9.71607 53.9345 14.6306 55.9694 19.8564 55.9694H27.3436C31.7435 55.9694 35.8789 54.2564 38.9894 51.1459ZM45.6296 22.0907L47.3087 23.7698C51.3953 27.8564 51.3953 34.5051 47.3087 38.5917L36.8719 49.0285C34.3268 51.5736 30.9432 52.9754 27.3436 52.9754L19.8564 52.9754C15.431 52.9754 11.266 51.2497 8.13648 48.1201C5.00694 44.9906 3.28124 40.8256 3.28124 36.4002L3.28124 15.3362C3.28124 13.3077 5.04717 11.5417 7.07777 11.5396C9.16977 11.5396 10.8743 13.2442 10.8743 15.3362V16.3568C10.8743 16.7718 11.0437 17.1444 11.3147 17.4155C11.5857 17.6865 11.9605 17.8538 12.3734 17.8559C13.1992 17.8559 13.8704 17.1847 13.8704 16.3589L13.8704 10.0447C13.8704 7.95273 15.575 6.24821 17.667 6.24821C18.6791 6.24821 19.634 6.64416 20.3497 7.35985C21.0654 8.07554 21.4614 9.03049 21.4614 10.0426L21.4614 16.3568C21.4614 16.7718 21.6308 17.1444 21.9018 17.4155C22.1728 17.6865 22.5455 17.8559 22.9605 17.8559C23.7863 17.8559 24.4575 17.1847 24.4575 16.3589L24.4554 6.86649C24.4554 4.77449 26.1599 3.06997 28.2519 3.06997C30.3439 3.06997 32.0485 4.77449 32.0485 6.8665L32.0485 16.3568C32.0485 16.7718 32.2179 17.1444 32.4889 17.4155C32.7599 17.6865 33.1347 17.8538 33.5476 17.8559C34.3734 17.8559 35.0446 17.1847 35.0446 16.3589L35.0446 11.1034C35.0446 9.07496 36.8105 7.30903 38.8411 7.30691C39.8533 7.30691 40.8082 7.70287 41.5239 8.41856C42.2396 9.13424 42.6355 10.0892 42.6355 11.1013L42.6355 26.9438C42.6355 27.3588 42.8049 27.7315 43.076 28.0025C43.347 28.2736 43.7218 28.4408 44.1347 28.443C44.9605 28.443 45.6317 27.7717 45.6317 26.946L45.6296 22.0907Z"
                    fill="#0C0B0F"
                  />
                </svg>
                <Icon.Triangle className={`${styles.triangle} ${styles.arrowUp}`} />
                <Icon.Triangle className={`${styles.triangle} ${styles.arrowRight}`} />
                <Icon.Triangle className={`${styles.triangle} ${styles.arrowDown}`} />
                <Icon.Triangle className={`${styles.triangle} ${styles.arrowLeft}`} />
              </div>
              <div className={`${styles.instructionText} ${styles.rotateText}`}>
                <p>Klikk og dra for å se deg rundt</p>
              </div>
            </div>
            <div className={styles.instruction}>
              <div className={`${styles.instructionImage} ${styles.navigateImage}`}>
                <svg
                  width="100"
                  height="65"
                  viewBox="0 0 200 129"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M100.013 128.17C155.233 128.17 199.998 99.6637 199.998 64.4999C199.998 29.336 155.233 0.830078 100.013 0.830078C44.7922 0.830078 0.0273438 29.336 0.0273438 64.4999C0.0273438 99.6637 44.7922 128.17 100.013 128.17ZM100.014 120.211C148.462 120.211 187.737 95.2683 187.737 64.4999C187.737 33.7315 148.462 8.7888 100.014 8.7888C51.5657 8.7888 12.2908 33.7315 12.2908 64.4999C12.2908 95.2683 51.5657 120.211 100.014 120.211Z"
                    fill="#1D1D1D"
                  />
                  <ellipse
                    cx="100.014"
                    cy="62.511"
                    rx="13.2056"
                    ry="8.35666"
                    fill="#090909"
                    fillOpacity="0.8"
                  />
                </svg>
              </div>
              <div className={`${styles.instructionText} ${styles.navigateText}`}>
                <p>Klikk på gulvet for å endre lokasjon</p>
              </div>
            </div>
          </>
        }
      </div>
      <div className={styles.label}>
        Dette er en virtuell opplevelse. Her kan du bevege deg rundt i et 3D-rom og interagere med gjenstander, bilder, video og lyd.
      </div>
      <Button
        label="Skjønner!"
        className={styles.button}
        onClick={onClose}
      />
    </Dialog>
  );
};
